"use client"

import * as React from "react"
import { differenceInDays, addDays, subDays, format, parse } from "date-fns"
import { Button } from "./button-nw"
import { Calendar } from "./calendar-nw"
import { Popover, PopoverContent, PopoverTrigger } from "./popover-nw"
import { Input } from "@/components/ui/input"
import { cn } from "@/lib/utils"
import { CalendarIcon } from "lucide-react"
import { type DateRange } from "react-day-picker"

// Predefined ranges
const predefinedRanges = [
  {
    label: "Next 7 days",
    getValue: () => ({ from: new Date(), to: addDays(new Date(), 7) }),
  },
  {
    label: "Next 15 days",
    getValue: () => ({ from: new Date(), to: addDays(new Date(), 15) }),
  },
  {
    label: "Next 30 days",
    getValue: () => ({ from: new Date(), to: addDays(new Date(), 30) }),
  },
  {
    label: "Previous 7 days",
    getValue: () => ({ from: subDays(new Date(), 7), to: new Date() }),
  },
  {
    label: "Previous 15 days",
    getValue: () => ({ from: subDays(new Date(), 15), to: new Date() }),
  },
  {
    label: "Previous 30 days",
    getValue: () => ({ from: subDays(new Date(), 30), to: new Date() }),
  },
]

interface DateRangePickerProps extends React.HTMLAttributes<HTMLDivElement> {
  date?: DateRange
  onDateChange?: (date: DateRange | undefined) => void
  error?: string
}

export default function DateRangePicker({
  className,
  date,
  onDateChange,
  error,
}: DateRangePickerProps) {
  const [localDate, setLocalDate] = React.useState<DateRange | undefined>(date)
  const [open, setOpen] = React.useState(false)
  const [startInputValue, setStartInputValue] = React.useState("")
  const [endInputValue, setEndInputValue] = React.useState("")

  React.useEffect(() => {
    if (date?.from) {
      setStartInputValue(format(date.from, "MM/dd/yyyy"))
    }
    if (date?.to) {
      setEndInputValue(format(date.to, "MM/dd/yyyy"))
    }
  }, [date])

  const handleManualDateInput = (value: string, isStart: boolean) => {
    try {
      const parsedDate = parse(value, "MM/dd/yyyy", new Date())
      if (!isNaN(parsedDate.getTime())) {
        const newDate = {
          from: isStart ? parsedDate : localDate?.from,
          to: isStart ? localDate?.to : parsedDate,
        }

        if (newDate.from && newDate.to) {
          const daysDiff = differenceInDays(newDate.to, newDate.from)
          if (daysDiff <= 30 && daysDiff >= 0) {
            setLocalDate(newDate as DateRange)
          }
        }
      }
    } catch (error) {
      console.error("Date parsing error:", error)
    }
  }

  const handlePredefinedRange = (range: (typeof predefinedRanges)[0]) => {
    const newDate = range.getValue()
    setLocalDate(newDate)
    setStartInputValue(format(newDate.from, "MM/dd/yyyy"))
    setEndInputValue(format(newDate.to, "MM/dd/yyyy"))
  }

  return (
    <div className={cn("grid gap-2", className)}>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            id="date"
            variant={error ? "destructive" : "outline"}
            className={cn(
              "w-[300px] justify-start text-left font-normal",
              !localDate && "text-muted-foreground",
              error && "border-red-500"
            )}
          >
            <CalendarIcon className="mr-2 h-4 w-4" />
            {localDate?.from ? (
              localDate.to ? (
                <>
                  {format(localDate.from, "LLL dd, y")} -{" "}
                  {format(localDate.to, "LLL dd, y")}
                </>
              ) : (
                format(localDate.from, "LLL dd, y")
              )
            ) : (
              <span>Pick a date range</span>
            )}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="start">
          <div className="p-3">
            {/* Manual date inputs */}
            <div className="flex gap-2 mb-3">
              <div className="grid gap-1">
                <label className="text-sm">Start Date</label>
                <Input
                  type="text"
                  placeholder="MM/DD/YYYY"
                  value={startInputValue}
                  onChange={(e) => {
                    setStartInputValue(e.target.value)
                    handleManualDateInput(e.target.value, true)
                  }}
                  className="w-[130px]"
                />
              </div>
              <div className="grid gap-1">
                <label className="text-sm">End Date</label>
                <Input
                  type="text"
                  placeholder="MM/DD/YYYY"
                  value={endInputValue}
                  onChange={(e) => {
                    setEndInputValue(e.target.value)
                    handleManualDateInput(e.target.value, false)
                  }}
                  className="w-[130px]"
                />
              </div>
            </div>

            {/* Predefined ranges */}
            <div className="grid grid-cols-2 gap-2 mb-3">
              {predefinedRanges.map((range) => (
                <Button
                  key={range.label}
                  variant="outline"
                  size="sm"
                  onClick={() => handlePredefinedRange(range)}
                  className="text-sm"
                >
                  {range.label}
                </Button>
              ))}
            </div>

            <Calendar
              mode="range"
              defaultMonth={localDate?.from}
              selected={localDate}
              onSelect={setLocalDate}
              numberOfMonths={2}
              disabled={(date) => {
                if (!localDate?.from) return false
                return differenceInDays(date, localDate.from) > 30
              }}
            />

            {error && <p className="text-sm text-red-500 mt-2 px-2">{error}</p>}

            <div className="mt-4 flex items-center justify-end gap-2 border-t pt-4 px-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setOpen(false)}
              >
                Cancel
              </Button>
              <Button
                size="sm"
                disabled={!localDate?.from || !localDate?.to}
                onClick={() => {
                  onDateChange?.(localDate)
                  setOpen(false)
                }}
              >
                Apply Range
              </Button>
            </div>
          </div>
        </PopoverContent>
      </Popover>
    </div>
  )
}
